import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  useTheme,
  Alert,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { X, Plus, Trash2 } from 'lucide-react';
import {
  FormField,
  FieldOption,
  DraggableFieldType,
  ValidationType,
  FieldValidation,
  ToolboxFieldType,
  ConditionalOperator,
  CONDITIONAL_SOURCE_TYPES,
  FILE_UPLOAD_WHITELIST,
  FILE_UPLOAD_MAX_FILES,
  FILE_UPLOAD_MAX_SIZE_MB,
} from '../../types/form';

// Detect effective toolbox type: number + rangeType -> 'range'
const getEffectiveToolboxType = (
  field: FormField | null,
  fieldType: DraggableFieldType | null
): ToolboxFieldType => {
  if (field) {
    // Editing existing field
    if (field.fieldType === 'number' && field.rangeType) return 'range';
    return field.fieldType;
  }
  if (fieldType) return fieldType.type;
  return 'text';
};

// Human-readable display name for each toolbox type
const TOOLBOX_TYPE_LABELS: Record<ToolboxFieldType, string> = {
  text: 'Text Input',
  textarea: 'Text Area',
  select: 'Dropdown',
  boolean: 'Toggle',
  number: 'Number',
  range: 'Range',
  email: 'Email',
  phone_number: 'Phone',
  checkbox: 'Checkbox',
  ai: 'AI Prompt',
  date: 'Date Picker',
  time: 'Time Picker',
  http_request: 'HTTP Request',
  file: 'File Upload',
  url: 'URL',
};

interface FieldConfigDialogProps {
  open: boolean;
  onClose: () => void;
  field: FormField | null;
  fieldType: DraggableFieldType | null;
  onSave: (field: FormField) => void;
  existingFieldIds: string[];
  allFields: FormField[];
}

// Operators offered per conditional-source field type. Checkbox answers
// arrive as a joined string ("val_a, val_b"), so only `contains` is reliable.
const OPERATORS_BY_SOURCE_TYPE: Record<string, { value: ConditionalOperator; label: string }[]> = {
  select: [
    { value: 'equals', label: 'is' },
    { value: 'notEquals', label: 'is not' },
    { value: 'in', label: 'is one of' },
  ],
  boolean: [{ value: 'equals', label: 'is' }],
  checkbox: [{ value: 'contains', label: 'includes' }],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'greaterThan', label: 'is greater than' },
    { value: 'lessThan', label: 'is less than' },
  ],
};

const VALIDATION_TYPES: { value: ValidationType; label: string; description: string }[] = [
  { value: 'MIN_LENGTH', label: 'Minimum Length', description: 'Minimum number of characters' },
  { value: 'MAX_LENGTH', label: 'Maximum Length', description: 'Maximum number of characters' },
  { value: 'EMAIL', label: 'Email Format', description: 'Must be a valid email address' },
  { value: 'PHONE', label: 'Phone Format', description: 'Must be a valid phone number' },
  { value: 'URL', label: 'URL Format', description: 'Must be a valid web address' },
  { value: 'REGEX', label: 'Custom Pattern', description: 'Custom regex pattern' },
  { value: 'NUMBER_RANGE', label: 'Number Range', description: 'Min and max number values' },
];

const FieldConfigDialog: React.FC<FieldConfigDialogProps> = ({
  open,
  onClose,
  field,
  fieldType,
  onSave,
  existingFieldIds,
  allFields,
}) => {
  const theme = useTheme();
  const isEditing = !!field;

  // Form state
  const [fieldName, setFieldName] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(false);
  const [placeholder, setPlaceholder] = useState('');
  const [options, setOptions] = useState<FieldOption[]>([]);
  const [validationType, setValidationType] = useState<ValidationType | ''>('');
  const [validationValue, setValidationValue] = useState<string | number>('');
  const [validationMin, setValidationMin] = useState<number | ''>('');
  const [validationMax, setValidationMax] = useState<number | ''>('');
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [multiSelect, setMultiSelect] = useState(false);
  const [rangeEnabled, setRangeEnabled] = useState(false);
  const [rangeMin, setRangeMin] = useState<number | ''>('');
  const [rangeMax, setRangeMax] = useState<number | ''>('');
  const [rangeStep, setRangeStep] = useState<number | ''>('');
  const [fileAcceptedTypes, setFileAcceptedTypes] = useState<string[]>([]);
  const [fileMaxFiles, setFileMaxFiles] = useState<number>(1);
  const [fileMaxSizeMb, setFileMaxSizeMb] = useState<number>(FILE_UPLOAD_MAX_SIZE_MB);
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [conditionalDependsOn, setConditionalDependsOn] = useState('');
  const [conditionalOperator, setConditionalOperator] = useState<ConditionalOperator>('equals');
  const [conditionalValue, setConditionalValue] = useState('');
  const [conditionalValues, setConditionalValues] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Get the effective toolbox type (detects number+rangeType as 'range')
  const effectiveType = getEffectiveToolboxType(field, fieldType);
  const isRange = effectiveType === 'range';
  // The persisted field type (always 'number' for range)
  const currentFieldType = isRange ? 'number' : field?.fieldType || fieldType?.type || 'text';

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      if (field) {
        // Editing existing field
        setFieldName(field.fieldId); // Field name is same as fieldId for existing
        setFieldId(field.fieldId);
        setLabel(field.label);
        setRequired(field.required);
        setPlaceholder(field.placeholder || '');
        setOptions(field.options || []);
        setMultiSelect(field.fieldType === 'checkbox');
        if (field.rangeType) {
          setRangeEnabled(true);
          setRangeMin(field.rangeType.min);
          setRangeMax(field.rangeType.max);
          setRangeStep(field.rangeType.step ?? '');
        } else {
          setRangeEnabled(false);
          setRangeMin('');
          setRangeMax('');
          setRangeStep('');
        }
        if (field.validation) {
          setValidationType(field.validation.type);
          setValidationValue(field.validation.value || '');
          setValidationMin(field.validation.min || '');
          setValidationMax(field.validation.max || '');
          setValidationErrorMessage(field.validation.errorMessage);
        } else {
          resetValidation();
        }
        setFileAcceptedTypes(field.fileConfig?.acceptedTypes || [...FILE_UPLOAD_WHITELIST]);
        setFileMaxFiles(field.fileConfig?.maxFiles || 1);
        setFileMaxSizeMb(field.fileConfig?.maxSizeMb || FILE_UPLOAD_MAX_SIZE_MB);
        if (field.conditional) {
          setConditionalEnabled(true);
          setConditionalDependsOn(field.conditional.dependsOn);
          setConditionalOperator(field.conditional.showWhen.operator);
          if (Array.isArray(field.conditional.showWhen.value)) {
            setConditionalValues(field.conditional.showWhen.value.map(String));
            setConditionalValue('');
          } else {
            setConditionalValue(String(field.conditional.showWhen.value));
            setConditionalValues([]);
          }
        } else {
          resetConditional();
        }
      } else if (fieldType) {
        // New field
        setFieldName('');
        setFieldId('');
        setLabel('');
        setRequired(fieldType.defaultField.required || false);
        setPlaceholder(fieldType.defaultField.placeholder || '');
        setOptions(fieldType.defaultField.options || []);
        setMultiSelect(false);
        // Auto-enable range for 'range' toolbox type
        if (fieldType.type === 'range') {
          setRangeEnabled(true);
          setRangeMin(fieldType.defaultField.rangeType?.min ?? 0);
          setRangeMax(fieldType.defaultField.rangeType?.max ?? 100);
          setRangeStep(fieldType.defaultField.rangeType?.step ?? '');
        } else {
          setRangeEnabled(false);
          setRangeMin('');
          setRangeMax('');
          setRangeStep('');
        }
        if (fieldType.defaultField.validation) {
          setValidationType(fieldType.defaultField.validation.type);
          setValidationErrorMessage(fieldType.defaultField.validation.errorMessage);
        } else {
          resetValidation();
        }
        setFileAcceptedTypes(
          fieldType.defaultField.fileConfig?.acceptedTypes || [...FILE_UPLOAD_WHITELIST]
        );
        setFileMaxFiles(fieldType.defaultField.fileConfig?.maxFiles || 1);
        setFileMaxSizeMb(fieldType.defaultField.fileConfig?.maxSizeMb || FILE_UPLOAD_MAX_SIZE_MB);
        resetConditional();
      }
      setError('');
    }
  }, [open, field, fieldType]);

  const resetValidation = () => {
    setValidationType('');
    setValidationValue('');
    setValidationMin('');
    setValidationMax('');
    setValidationErrorMessage('');
  };

  const resetConditional = () => {
    setConditionalEnabled(false);
    setConditionalDependsOn('');
    setConditionalOperator('equals');
    setConditionalValue('');
    setConditionalValues([]);
  };

  // Handle label change and auto-generate fieldName + fieldId from it
  const handleLabelChange = (value: string) => {
    setLabel(value);
    if (!isEditing) {
      // Generate fieldId from label: lowercase, no special chars, underscores for spaces
      const generatedId = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 30);
      setFieldName(value);
      setFieldId(generatedId);
    }
  };

  const handleAddOption = () => {
    setOptions([...options, { label: '', value: '' }]);
  };

  const handleOptionChange = (index: number, key: 'label' | 'value', value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [key]: value };

    // Auto-generate value from label
    if (key === 'label') {
      newOptions[index].value = value.toLowerCase().replace(/\s+/g, '_');
    }

    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Validation
    if (!label.trim()) {
      setError('Label is required');
      return;
    }

    // Ensure fieldId is generated from label
    const finalFieldId = isEditing
      ? fieldId.trim()
      : fieldId.trim() ||
        label
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 30);

    // Check for duplicate fieldId (only for new fields)
    if (!isEditing && existingFieldIds.includes(finalFieldId)) {
      setError('A field with a similar name already exists. Please use a different label.');
      return;
    }

    // Check select options
    if (
      (currentFieldType === 'select' || currentFieldType === 'checkbox') &&
      options.length === 0
    ) {
      setError('Please add at least one option for dropdown field');
      return;
    }

    // Validate range if this is a range field
    if (isRange) {
      if (rangeMin === '' || rangeMax === '') {
        setError('Both Min and Max values are required for range');
        return;
      }
      if (Number(rangeMin) <= -100000) {
        setError('Min value must be greater than -100,000');
        return;
      }
      if (Number(rangeMax) >= 100000) {
        setError('Max value must be less than 100,000');
        return;
      }
      if (Number(rangeMax) <= Number(rangeMin)) {
        setError('Max value must be greater than Min value');
        return;
      }
      if (rangeStep !== '' && Number(rangeStep) <= 0) {
        setError('Step must be a positive number');
        return;
      }
    }

    // Validate file upload config
    if (currentFieldType === 'file' && fileAcceptedTypes.length === 0) {
      setError('Please select at least one accepted file type');
      return;
    }

    // Validate conditional visibility config
    if (conditionalEnabled) {
      if (!conditionalDependsOn) {
        setError('Please select the field this condition depends on');
        return;
      }
      const conditionValueMissing =
        conditionalOperator === 'in' ? conditionalValues.length === 0 : conditionalValue === '';
      if (conditionValueMissing) {
        setError('Please provide a value for the visibility condition');
        return;
      }
    }

    // Build validation object
    let validation: FieldValidation | undefined;
    if (validationType) {
      validation = {
        type: validationType,
        errorMessage: validationErrorMessage || `Invalid ${label}`,
      };
      if (validationType === 'MIN_LENGTH' || validationType === 'MAX_LENGTH') {
        validation.value = Number(validationValue);
      } else if (validationType === 'REGEX') {
        validation.value = String(validationValue);
      } else if (validationType === 'NUMBER_RANGE') {
        if (validationMin !== '') validation.min = Number(validationMin);
        if (validationMax !== '') validation.max = Number(validationMax);
      }
    }

    // Determine the final field type based on multiSelect toggle
    const isDropdownType = currentFieldType === 'select' || currentFieldType === 'checkbox';
    const finalFieldType = isDropdownType
      ? multiSelect
        ? 'checkbox'
        : 'select'
      : currentFieldType;

    const savedField: FormField = {
      fieldId: finalFieldId,
      label: label.trim(),
      fieldType: finalFieldType,
      required: currentFieldType === 'boolean' ? false : required,
      placeholder: placeholder || undefined,
      options: isDropdownType ? options.filter(o => o.label && o.value) : undefined,
      validation,
      rangeType: isRange
        ? {
            min: Number(rangeMin),
            max: Number(rangeMax),
            ...(rangeStep !== '' ? { step: Number(rangeStep) } : {}),
          }
        : undefined,
      fileConfig:
        currentFieldType === 'file'
          ? {
              acceptedTypes: fileAcceptedTypes,
              maxFiles: Math.min(Math.max(fileMaxFiles || 1, 1), FILE_UPLOAD_MAX_FILES),
              maxSizeMb: Math.min(Math.max(fileMaxSizeMb || 1, 1), FILE_UPLOAD_MAX_SIZE_MB),
            }
          : undefined,
      conditional:
        conditionalEnabled && conditionalDependsOn
          ? {
              dependsOn: conditionalDependsOn,
              showWhen: {
                operator: conditionalOperator,
                value: conditionalOperator === 'in' ? conditionalValues : conditionalValue,
              },
            }
          : undefined,
      order: field?.order || 0,
    };

    onSave(savedField);
  };

  const showPlaceholder =
    !isRange &&
    ['text', 'textarea', 'email', 'phone_number', 'number', 'url'].includes(currentFieldType);
  const showOptions = currentFieldType === 'select' || currentFieldType === 'checkbox';
  const showValidation =
    !isRange &&
    ['text', 'textarea', 'email', 'phone_number', 'number', 'url'].includes(currentFieldType);
  const showFileConfig = currentFieldType === 'file';

  // Conditional visibility: only earlier deterministic fields can be sources
  const currentOrder = field?.order ?? allFields.length + 1;
  const eligibleSourceFields = allFields.filter(
    f =>
      f.order < currentOrder &&
      f.fieldId !== field?.fieldId &&
      CONDITIONAL_SOURCE_TYPES.includes(f.fieldType)
  );
  const showConditional = eligibleSourceFields.length > 0;
  const conditionalSourceField = eligibleSourceFields.find(f => f.fieldId === conditionalDependsOn);
  const sourceOperators = conditionalSourceField
    ? OPERATORS_BY_SOURCE_TYPE[conditionalSourceField.fieldType] || []
    : [];

  const handleConditionalSourceChange = (fieldId: string) => {
    setConditionalDependsOn(fieldId);
    const source = eligibleSourceFields.find(f => f.fieldId === fieldId);
    const operators = source ? OPERATORS_BY_SOURCE_TYPE[source.fieldType] || [] : [];
    setConditionalOperator(operators[0]?.value || 'equals');
    setConditionalValue('');
    setConditionalValues([]);
  };

  const filteredValidationTypes = VALIDATION_TYPES;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {isEditing
            ? `Edit ${TOOLBOX_TYPE_LABELS[effectiveType]}`
            : `Add ${TOOLBOX_TYPE_LABELS[effectiveType]}`}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Basic Info */}
          <TextField
            label="Label"
            value={label}
            onChange={e => handleLabelChange(e.target.value)}
            fullWidth
            required
            placeholder="e.g., What is your name?"
            helperText={
              isEditing
                ? 'The question or label shown to users'
                : `The question or label shown to users (Field ID: ${fieldId || '...'})`
            }
          />

          {/* Placeholder */}
          {showPlaceholder && (
            <TextField
              label="Placeholder"
              value={placeholder}
              onChange={e => setPlaceholder(e.target.value)}
              fullWidth
              placeholder="e.g., Enter your answer..."
              helperText="Hint text shown when field is empty"
            />
          )}

          {currentFieldType !== 'boolean' && (
            <FormControlLabel
              control={<Switch checked={required} onChange={e => setRequired(e.target.checked)} />}
              label="Required field"
            />
          )}

          {/* Range Settings (shown directly for range fields) */}
          {isRange && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Range Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Min Value"
                    type="number"
                    value={rangeMin}
                    onChange={e => setRangeMin(e.target.value ? Number(e.target.value) : '')}
                    size="small"
                    sx={{ flex: 1 }}
                    helperText="Must be greater than -100,000"
                  />
                  <TextField
                    label="Max Value"
                    type="number"
                    value={rangeMax}
                    onChange={e => setRangeMax(e.target.value ? Number(e.target.value) : '')}
                    size="small"
                    sx={{ flex: 1 }}
                    helperText="Must be less than 100,000"
                  />
                </Box>
                <TextField
                  label="Incremental Step"
                  type="number"
                  value={rangeStep}
                  onChange={e => setRangeStep(e.target.value ? Number(e.target.value) : '')}
                  size="small"
                  placeholder="e.g., 1, 0.5, 10"
                  helperText="Step size for the slider (leave empty for auto)"
                />
              </Box>
            </Box>
          )}

          {/* Options for Select */}
          {showOptions && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Dropdown Options
              </Typography>

              <FormControlLabel
                control={
                  <Switch checked={multiSelect} onChange={e => setMultiSelect(e.target.checked)} />
                }
                label="Multi-Select (allows selecting multiple options)"
                sx={{ mb: 2 }}
              />

              {options.map((option, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 1,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    label="Label"
                    value={option.label}
                    onChange={e => handleOptionChange(index, 'label', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value"
                    value={option.value}
                    onChange={e => handleOptionChange(index, 'value', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" color="error" onClick={() => handleRemoveOption(index)}>
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
              ))}

              <Button
                variant="outlined"
                size="small"
                startIcon={<Plus size={16} />}
                onClick={handleAddOption}
                sx={{ mt: 1 }}
              >
                Add Option
              </Button>
            </Box>
          )}

          {/* File Upload Settings */}
          {showFileConfig && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                File Upload Settings
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Accepted file types
              </Typography>
              <FormGroup row sx={{ mb: 2 }}>
                {FILE_UPLOAD_WHITELIST.map(ext => (
                  <FormControlLabel
                    key={ext}
                    control={
                      <Checkbox
                        size="small"
                        checked={fileAcceptedTypes.includes(ext)}
                        onChange={e =>
                          setFileAcceptedTypes(prev =>
                            e.target.checked ? [...prev, ext] : prev.filter(t => t !== ext)
                          )
                        }
                      />
                    }
                    label={`.${ext}`}
                  />
                ))}
              </FormGroup>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Max Files"
                  type="number"
                  value={fileMaxFiles}
                  onChange={e => setFileMaxFiles(Number(e.target.value))}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, max: FILE_UPLOAD_MAX_FILES }}
                  helperText={`Allow multiple files (1–${FILE_UPLOAD_MAX_FILES})`}
                />
                <TextField
                  label="Max Size per File (MB)"
                  type="number"
                  value={fileMaxSizeMb}
                  onChange={e => setFileMaxSizeMb(Number(e.target.value))}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, max: FILE_UPLOAD_MAX_SIZE_MB }}
                  helperText={`Up to ${FILE_UPLOAD_MAX_SIZE_MB}MB`}
                />
              </Box>
            </Box>
          )}

          {/* Validation */}
          {showValidation && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Validation (Optional)
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Validation Type</InputLabel>
                <Select
                  value={validationType}
                  label="Validation Type"
                  onChange={e => {
                    setValidationType(e.target.value as ValidationType | '');
                    setValidationValue('');
                    setValidationMin('');
                    setValidationMax('');
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {filteredValidationTypes.map(vt => (
                    <MenuItem key={vt.value} value={vt.value}>
                      {vt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(validationType === 'MIN_LENGTH' || validationType === 'MAX_LENGTH') && (
                <TextField
                  label="Character Count"
                  type="number"
                  value={validationValue}
                  onChange={e => setValidationValue(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
              )}

              {validationType === 'REGEX' && (
                <TextField
                  label="Regex Pattern"
                  value={validationValue}
                  onChange={e => setValidationValue(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  placeholder="e.g., ^[A-Z]{2}[0-9]{4}$"
                />
              )}

              {validationType === 'NUMBER_RANGE' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Min Value"
                    type="number"
                    value={validationMin}
                    onChange={e => setValidationMin(e.target.value ? Number(e.target.value) : '')}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Value"
                    type="number"
                    value={validationMax}
                    onChange={e => setValidationMax(e.target.value ? Number(e.target.value) : '')}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}

              {validationType && (
                <TextField
                  label="Error Message"
                  value={validationErrorMessage}
                  onChange={e => setValidationErrorMessage(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g., Please enter a valid value"
                />
              )}
            </Box>
          )}

          {/* Conditional Visibility */}
          {showConditional && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Conditional Visibility
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={conditionalEnabled}
                    onChange={e => setConditionalEnabled(e.target.checked)}
                  />
                }
                label="Show this field only when a condition is met"
              />

              {conditionalEnabled && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Depends on field</InputLabel>
                    <Select
                      value={conditionalDependsOn}
                      label="Depends on field"
                      onChange={e => handleConditionalSourceChange(e.target.value)}
                    >
                      {eligibleSourceFields.map(f => (
                        <MenuItem key={f.fieldId} value={f.fieldId}>
                          {f.label} ({f.fieldId})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {conditionalSourceField && (
                    <>
                      <FormControl fullWidth size="small">
                        <InputLabel>Condition</InputLabel>
                        <Select
                          value={conditionalOperator}
                          label="Condition"
                          onChange={e => {
                            setConditionalOperator(e.target.value as ConditionalOperator);
                            setConditionalValue('');
                            setConditionalValues([]);
                          }}
                        >
                          {sourceOperators.map(op => (
                            <MenuItem key={op.value} value={op.value}>
                              {op.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Value input per source type */}
                      {conditionalSourceField.fieldType === 'boolean' ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Value</InputLabel>
                          <Select
                            value={conditionalValue}
                            label="Value"
                            onChange={e => setConditionalValue(e.target.value)}
                          >
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </Select>
                        </FormControl>
                      ) : conditionalSourceField.fieldType === 'number' ? (
                        <TextField
                          label="Value"
                          type="number"
                          value={conditionalValue}
                          onChange={e => setConditionalValue(e.target.value)}
                          fullWidth
                          size="small"
                        />
                      ) : conditionalOperator === 'in' ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Values</InputLabel>
                          <Select
                            multiple
                            value={conditionalValues}
                            label="Values"
                            onChange={e =>
                              setConditionalValues(
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : e.target.value
                              )
                            }
                          >
                            {(conditionalSourceField.options || []).map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <FormControl fullWidth size="small">
                          <InputLabel>Value</InputLabel>
                          <Select
                            value={conditionalValue}
                            label="Value"
                            onChange={e => setConditionalValue(e.target.value)}
                          >
                            {(conditionalSourceField.options || []).map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {!conditionalSourceField.required && (
                        <Alert severity="info">
                          If the user skips this optional field, this field will stay hidden.
                        </Alert>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {isEditing
            ? `Update ${TOOLBOX_TYPE_LABELS[effectiveType]}`
            : `Add ${TOOLBOX_TYPE_LABELS[effectiveType]}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldConfigDialog;
